import { appConfig } from "../../../../app.config";
import { fontFaceCss } from "./fonts";
import type { PosterLayout, Tier } from "./layout";
import type { PosterTheme } from "./themes";

/**
 * Le dessin de l'affiche : un SVG autonome, construit en texte a partir de la mise en page
 * (layout.ts) et d'un theme (themes.ts). Le meme SVG sert a l'apercu (dans une balise
 * <img>, donc sans script ni acces a la page) et a l'export PNG (export.ts). Les polices
 * du theme y sont encodees : une image SVG n'utilise pas les polices de la page.
 *
 * Les couleurs du theme sont posees en variables CSS sur la racine du SVG.
 */

/** Echappe un texte pour un contenu ou un attribut XML. */
export function escapeXml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function fmt(value: number): string {
  return Number(value.toFixed(2)).toString();
}

const TIER_VAR: Record<Tier, string> = { 0: "--head", 1: "--mid", 2: "--small" };

function star(cx: number, cy: number, r: number): string {
  const points: string[] = [];
  for (let i = 0; i < 10; i += 1) {
    const radius = i % 2 === 0 ? r : r * 0.45;
    const angle = (Math.PI / 5) * i - Math.PI / 2;
    points.push(`${fmt(cx + radius * Math.cos(angle))},${fmt(cy + radius * Math.sin(angle))}`);
  }
  return `<polygon points="${points.join(" ")}" style="fill:var(--ornament)"/>`;
}

function background(layout: PosterLayout, theme: PosterTheme): string {
  const { width, height } = layout;
  const stops = theme.colors.background;
  const parts: string[] = [];
  if (stops.length > 1) {
    const gradient = stops
      .map(
        (color, index) =>
          `<stop offset="${fmt(index / (stops.length - 1))}" stop-color="${color}"/>`,
      )
      .join("");
    parts.push(
      `<defs><linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">${gradient}</linearGradient></defs>`,
      `<rect width="${width}" height="${height}" fill="url(#bg)"/>`,
    );
  } else {
    parts.push(`<rect width="${width}" height="${height}" fill="${stops[0] ?? "#000"}"/>`);
  }
  return parts.join("");
}

function ornaments(layout: PosterLayout, theme: PosterTheme): string {
  const { width, height, ruleY } = layout;
  if (theme.id === "nuit") {
    return [
      `<rect x="36" y="36" width="${width - 72}" height="${height - 72}" fill="none" style="stroke:var(--ornament)" stroke-width="3"/>`,
      `<line x1="250" y1="${fmt(ruleY)}" x2="${width - 250}" y2="${fmt(ruleY)}" style="stroke:var(--ornament)" stroke-width="3"/>`,
      star(210, ruleY, 14),
      star(width - 210, ruleY, 14),
    ].join("");
  }
  if (theme.id === "ete") {
    return [
      `<circle cx="${width / 2}" cy="${fmt(ruleY - 40)}" r="230" style="fill:var(--ornament)" fill-opacity="0.28"/>`,
      `<path d="M0 ${height - 150} Q ${width / 4} ${height - 200} ${width / 2} ${height - 150} T ${width} ${height - 150} V ${height} H 0 Z" fill="#ffffff" fill-opacity="0.22"/>`,
      `<line x1="300" y1="${fmt(ruleY)}" x2="${width - 300}" y2="${fmt(ruleY)}" style="stroke:var(--title)" stroke-width="4" stroke-linecap="round"/>`,
    ].join("");
  }
  return [
    `<rect x="30" y="30" width="${width - 60}" height="${height - 60}" fill="none" style="stroke:var(--ornament)" stroke-width="6"/>`,
    `<rect x="46" y="46" width="${width - 92}" height="${height - 92}" fill="none" style="stroke:var(--ornament)" stroke-width="2"/>`,
    `<line x1="120" y1="${fmt(ruleY - 5)}" x2="${width - 120}" y2="${fmt(ruleY - 5)}" style="stroke:var(--title)" stroke-width="4"/>`,
    `<line x1="120" y1="${fmt(ruleY + 5)}" x2="${width - 120}" y2="${fmt(ruleY + 5)}" style="stroke:var(--title)" stroke-width="1.5"/>`,
    star(width / 2, ruleY, 16),
  ].join("");
}

export interface PosterSvgOptions {
  layout: PosterLayout;
  theme: PosterTheme;
  /** Encoder les polices du theme dans le SVG (apercu comme export : oui). */
  embedFonts?: boolean;
}

/** Le SVG complet de l'affiche, en texte. */
export function buildPosterSvg({ layout, theme, embedFonts = true }: PosterSvgOptions): string {
  const { width, height } = layout;
  const c = theme.colors;
  const vars = `--title:${c.title};--head:${c.head};--mid:${c.mid};--small:${c.small};--separator:${c.separator};--footer:${c.footer};--ornament:${c.ornament}`;
  const fontCss = embedFonts ? fontFaceCss(theme.font) : "";
  const style = `<style>${fontCss}text{font-family:"${theme.font}",sans-serif;text-anchor:middle}</style>`;

  const title = `<text x="${width / 2}" y="${fmt(layout.title.y)}" font-size="${fmt(layout.title.size)}" font-weight="${theme.strongWeight}" letter-spacing="${fmt(theme.titleTracking * layout.title.size)}" style="fill:var(--title)">${escapeXml(layout.title.text)}</text>`;

  const rows = layout.rows
    .map((row) => {
      const weight = row.tier === 2 ? theme.lightWeight : theme.strongWeight;
      const spans = row.names
        .map((name, index) => {
          const separator =
            index === 0
              ? ""
              : `<tspan style="fill:var(--separator)">${escapeXml(layout.separator)}</tspan>`;
          return `${separator}<tspan>${escapeXml(name)}</tspan>`;
        })
        .join("");
      return `<text x="${width / 2}" y="${fmt(row.y)}" font-size="${fmt(row.size)}" font-weight="${weight}" xml:space="preserve" style="fill:var(${TIER_VAR[row.tier]})">${spans}</text>`;
    })
    .join("");

  const footer = `<text x="${width / 2}" y="${fmt(layout.footer.y)}" font-size="${layout.footer.size}" font-weight="${theme.lightWeight}" letter-spacing="3" style="fill:var(--footer)">${escapeXml(appConfig.domain)}</text>`;

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" style="${vars}">`,
    style,
    background(layout, theme),
    ornaments(layout, theme),
    title,
    rows,
    footer,
    "</svg>",
  ].join("");
}

/** Le SVG en adresse data:, pour une balise <img> ou un canvas. */
export function svgDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export interface PosterPreviewProps {
  svg: string;
  label: string;
}

/** L'apercu de l'affiche dans la page. */
export function PosterPreview({ svg, label }: PosterPreviewProps) {
  return (
    <img
      alt={label}
      className="mx-auto block h-auto w-full rounded-md shadow-lg lg:max-h-[calc(100vh-11rem)] lg:w-auto lg:max-w-full"
      height={1350}
      src={svgDataUrl(svg)}
      width={1080}
    />
  );
}
